import {useCallback, useEffect} from 'react';
import css from 'styled-jsx/css';
import {
  getStaticAssetUrl,
  StaticAssetType,
} from '../../../../../../common/shared/utils/getStaticAssetUrl';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {
  FONT_FAMILY_FILE_NAME,
  PREVENT_SCROLL_CLASSNAME,
} from '../constant/talkViewerConstant';
import {setFontLoadStatus} from '../state/talkViewerSlice';
import {FontLoadStatus} from '../type/FontLoadStatus.enum';
import {TalkViewerFontFamilyType} from '../type/TalkViewerFontFamilyType.enum';

export function TalkViewerGlobalStyleContainer() {
  const preloadedMedia = useTypedSelector(
    state => state.talkViewer.preloadedMedia
  );
  const fontLoadStatus = useTypedSelector(
    state => state.talkViewer.fontLoadStatus
  );
  const getFontUrl = useCallback(
    (fontName: string) => {
      const fontPath = getStaticAssetUrl(fontName, StaticAssetType.Font);
      if (preloadedMedia?.[fontPath]?.dataUrl) {
        return preloadedMedia[fontPath].dataUrl;
      }
      return fontPath;
    },
    [preloadedMedia]
  );

  const dispatch = useTypedDispatch();

  useEffect(() => {
    if (fontLoadStatus !== FontLoadStatus.BeforeLoad) {
      return;
    }
    // font가 확실히 로드 되었는지 확인하기 위한 코드. 텍스트 줄바꿈 로직을 실행할 때 폰트로딩이 되어있지 않으면 기본폰트로 줄바꿈을 해서
    // 줄바꿈 오류가 일어난다. 그래서 draft인 fontface API를 사용해서 확인한다. 미지원(Fail) 브라우저는 현재 확인되진 않았지만, 이 경우 setTimeout으로 줄바꿈 할 예정
    const loadAllFontPromise = Promise.all(
      Object.values(TalkViewerFontFamilyType).map(font => {
        return new Promise<void>((resolve, reject) => {
          try {
            const fontName = font as TalkViewerFontFamilyType;
            const url = getFontUrl(FONT_FAMILY_FILE_NAME[font]);
            if (!('FontFace' in window)) {
              reject();
            }
            const fontFace = new FontFace(fontName, `url(${url})`);
            fontFace.load().then(() => {
              document.fonts?.add(fontFace);
              resolve();
            });
          } catch {
            reject();
          }
        });
      })
    );
    // 3초 이상 폰트를 모두 로딩하지 못하면 정상적이지 못한 상황으로 가정하고 promise를 reject시킨다.
    // (폰트파일은 우리 static host에있고, preload한 폰트를 사용하기에 사실 가능성은 거의 없다. 이 경우면 사실상 웹폰트를 못 불러온 경우)
    const timeoutFallbackPromise = new Promise((_, reject) => {
      setTimeout(reject, 3000);
    });
    // 폰트 로딩과 타임아웃 중 빠른 쪽의 결과를 가지고 처리한다.
    Promise.race([loadAllFontPromise, timeoutFallbackPromise])
      .then(() => dispatch(setFontLoadStatus(FontLoadStatus.Success)))
      .catch(() => dispatch(setFontLoadStatus(FontLoadStatus.Fail)));
  }, [fontLoadStatus, getFontUrl, dispatch]);
  const fontStyle = css`
    ${Object.values(TalkViewerFontFamilyType).reduce((acc, font) => {
      const fontUrl = getFontUrl(FONT_FAMILY_FILE_NAME[font]);
      // WEB_STATIC_HOST에서 폰트를 가져오는 경우 cors에러 등을 대비해 fallback으로 WEB_HOST에서 직접 가져올 수 있게 해 준다.
      const directFontUrl = `/public/font/${font}`;
      acc += `
        @font-face {
          font-family: ${font};
          font-style: normal;
          src: url(${fontUrl}) format('opentype')${
            directFontUrl ? `, url(${directFontUrl}) format('opentype')` : ''
          };
        }`;
      return acc;
    }, '')}
  `;
  return (
    <>
      <style jsx global>
        {`
          html {
            overscroll-behavior-y: none;
          }
          body {
            overscroll-behavior-y: none;
            overflow-x: hidden;
          }
          body.${PREVENT_SCROLL_CLASSNAME} {
            height: 100%;
            overflow-y: hidden;
          }
          * {
            -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
            -webkit-touch-callout: none;
            user-select: none;
            touch-action: pan-y pan-x;
          }
        `}
      </style>
      <style jsx global>
        {fontStyle}
      </style>
    </>
  );
}
